import React, { useState, useEffect } from 'react';  
import './Disc.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {   Elements,  CardElement,  PaymentRequestButtonElement,  useStripe,  useElements } from '@stripe/react-stripe-js';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_your_key_here';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fae8ff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Payment Form Component
const PaymentForm = ({ tier, onSuccess, onCancel }) => {
  console.debug("payment page for tier: " + tier);

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  // Get user info from localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Decode token to get email (basic JWT decode)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setEmail(payload.email || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  // Setup Apple Pay / Google Pay
  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: `${tier.name} Plan - Monthly`,
        amount: tier.price * 100, // Amount in cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if browser supports Apple Pay / Google Pay
    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    pr.on('paymentmethod', async (event) => {
      // Handle payment
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            tier: tier.id,
            paymentMethodId: event.paymentMethod.id
          })
        });

        const { clientSecret, error: backendError } = await response.json();

        if (backendError) {
          event.complete('fail');
          setError(backendError);
          setLoading(false);
          return;
        }

        // Confirm payment
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          event.complete('fail');
          setError(confirmError.message);
          setLoading(false);
        } else {
          event.complete('success');
          if (paymentIntent.status === 'succeeded') {
            onSuccess(paymentIntent);
          }
        }
      } catch (err) {
        event.complete('fail');
        setError('Payment failed. Please try again.');
        setLoading(false);
      }
    });
  }, [stripe, tier, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      // Create payment intent
      const response = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier: tier.id,
          email,
          name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret, subscriptionId } = await response.json();

      // Confirm payment with card element
      const cardElement = elements.getElement(CardElement);
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name,
              email,
            },
          },
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        setLoading(false);
      } else {
        if (paymentIntent.status === 'succeeded') {
          onSuccess({ paymentIntent, subscriptionId });
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      {/* Apple Pay / Google Pay Button */}
      {canMakePayment && paymentRequest && (
        <div className="digital-wallet-section">
          <PaymentRequestButtonElement
            options={{ paymentRequest }}
            className="payment-request-button"
          />
          <div className="divider">
            <span>or pay with card</span>
          </div>
        </div>
      )}

      {/* Card Payment Form */}
      <div className="form-group">
        <label htmlFor="name">
          <span className="label-icon">👤</span>
          Cardholder Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="John Doe"
          required
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">
          <span className="label-icon">📧</span>
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="john@example.com"
          required
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label>
          <span className="label-icon">💳</span>
          Card Information
        </label>
        <div className="card-element-container">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          disabled={!stripe || loading}
          className="pay-btn"
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Processing...
            </>
          ) : (
            <>
              <span>💳</span>
              Pay ${tier.price}/month
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="cancel-btn"
        >
          Cancel
        </button>
      </div>

      <div className="secure-notice">
        <span className="lock-icon">🔒</span>
        <p>Your payment information is secure and encrypted</p>
      </div>
    </form>
  );
}; 


// Main Payment Page Component
const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tier, setTier] = useState(null);

  useEffect(() => {
    // Get tier from navigation state
    const selectedTier = location.state?.tier;
    
    if (!selectedTier) {
      // If no tier selected, redirect to home
      navigate('/');
      return;
    }

    setTier(selectedTier);
  }, [location, navigate]);

  const handlePaymentSuccess = async ({ paymentIntent, subscriptionId }) => {
    console.log('Payment successful:', paymentIntent);

    // Update user tier in backend
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/user/upgrade-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tier: tier.id,
          subscriptionId,
          paymentIntentId: paymentIntent.id
        })
      });

      // Redirect to success page
      navigate('/payment-success', {
        state: {
          tier: tier.name,
          amount: tier.price,
          paymentIntent
        }
      });
    } catch (err) {
      console.error('Error updating tier:', err);
      navigate('/payment-success', {
        state: {
          tier: tier.name,
          amount: tier.price,
          error: 'Payment succeeded but tier update failed. Please contact support.'
        }
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (!tier) {
    return (
      <div className="payment-page">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Back
          </button>
          <h1>Complete Your Purchase</h1>
          <p>Upgrade to {tier.name} Plan</p>
        </div>

        <div className="payment-layout">
          {/* Order Summary */}
          <div className="order-summary">
            <h2>Order Summary</h2>
            
            <div className="summary-card">
              <div className="tier-badge" data-tier={tier.id}>
                {tier.icon} {tier.name}
              </div>

              <div className="summary-details">
                <div className="summary-item">
                  <span>Plan</span>
                  <span className="price">${tier.price}/month</span>
                </div>

                <div className="features-list">
                  <h3>What's included:</h3>
                  <ul>
                    {tier.features?.map((feature, index) => (
                      <li key={index}>
                        <span className="check-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="summary-total">
                  <span>Total due today</span>
                  <span className="total-amount">${tier.price}</span>
                </div>

                <p className="billing-notice">
                  You'll be charged ${tier.price} monthly. Cancel anytime.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form-section">
            <h2>Payment Details</h2>
            <Elements stripe={stripePromise}>
              <PaymentForm
                tier={tier}
                onSuccess={handlePaymentSuccess}
                onCancel={handleCancel}
              />
            </Elements>
          </div>
        </div>

        <div className="trust-badges">
          <div className="badge">
            <span>🔒</span>
            <p>Secure Payment</p>
          </div>
          <div className="badge">
            <span>↩️</span>
            <p>Money-back Guarantee</p>
          </div>
          <div className="badge">
            <span>🛡️</span>
            <p>SSL Encrypted</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;